/**
 * Doserne Projects — catalog list + create/edit dialog for admins.
 */
import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, Search, Filter, FolderKanban, MapPin, User as UserIcon,
  ChevronRight, X, Building2, Edit3, Trash2, Download, RefreshCw, Settings, MessageCircle,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import DoserneShell from "./DoserneShell";
import TripletexImportModal from "./TripletexImportModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  active:   { label: "Aktiv",     dot: "bg-emerald-500" },
  on_hold:  { label: "På vent",   dot: "bg-amber-500" },
  done:     { label: "Ferdig",    dot: "bg-slate-400" },
  archived: { label: "Arkivert",  dot: "bg-slate-300" },
};

const empty = { number: "", name: "", customer_name: "", customer_email: "", customer_phone: "", address: "", status: "active", start_date: "", end_date: "", description: "" };

function ProjectDialog({ initial, onClose, onSaved, authHeader }) {
  const [form, setForm] = useState(initial || empty);
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/doserne/projects/${initial.id}`, form, authHeader());
      } else {
        await axios.post(`${API}/doserne/projects`, form, authHeader());
      }
      toast.success(isEdit ? "Prosjekt oppdatert" : "Prosjekt opprettet");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Feil ved lagring");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="project-dialog">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <h2 className="text-lg font-black text-slate-900">{isEdit ? "Rediger prosjekt" : "Nytt prosjekt"}</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-900" data-testid="project-dialog-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1">
              <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Nummer</label>
              <input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" placeholder="P-042" />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Navn *</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" data-testid="project-form-name" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Kunde</label>
            <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">E-post</label>
              <input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Telefon</label>
              <input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Adresse</label>
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" placeholder="Storgata 10, Oslo" data-testid="project-form-address" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-2 py-2 border border-slate-300 rounded-lg text-sm mt-1">
                {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Start</label>
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-full px-2 py-2 border border-slate-300 rounded-lg text-sm mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Slutt</label>
              <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="w-full px-2 py-2 border border-slate-300 rounded-lg text-sm mt-1" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Beskrivelse</label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" placeholder="Notater om jobben…" />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50">Avbryt</button>
            <button type="submit" disabled={busy} className="flex-1 py-2.5 rounded-lg bg-slate-900 disabled:bg-slate-400 text-sm font-semibold flex items-center justify-center gap-2" style={{ color: "#fff" }} data-testid="project-form-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Lagre"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DoserneProjects() {
  const { user, loading: authLoading, authHeader } = useDoserneAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [dialog, setDialog] = useState(null);
  const [showTripletex, setShowTripletex] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (status) params.set("status", status);
      const { data } = await axios.get(`${API}/doserne/projects?${params.toString()}`, authHeader());
      setProjects(data.projects || []);
    } catch { toast.error("Kunne ikke laste prosjekter"); }
    setLoading(false);
  };

  useEffect(() => { if (user) load(); }, [user, status]);

  // Live search — refetch 250 ms after the user stops typing so we don't
  // spam the API on every keystroke.
  useEffect(() => {
    if (!user) return;
    const t = setTimeout(() => { load(); }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const del = async (p) => {
    if (!window.confirm(`Slette prosjekt "${p.name}"?`)) return;
    try {
      await axios.delete(`${API}/doserne/projects/${p.id}`, authHeader());
      toast.success("Slettet");
      load();
    } catch { toast.error("Kunne ikke slette"); }
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;

  return (
    <DoserneShell title="Prosjekter" subtitle="Alle jobber vi jobber med — koblet til Underlag, Kontroll, SJA og progresjon.">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex-1 min-w-[220px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Søk navn, kunde, adresse…" className="w-full pl-9 pr-9 py-2.5 border border-slate-300 rounded-lg text-sm" data-testid="projects-search" />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700"
              title="Tøm søk"
              data-testid="projects-search-clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </form>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2.5 border border-slate-300 rounded-lg text-sm" data-testid="projects-status">
          <option value="">Alle status</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {user.is_admin && (
          <>
            <Link
              to="/doserne/tripletex-config"
              className="inline-flex items-center justify-center w-11 h-11 rounded-lg border border-slate-300 text-slate-500 hover:bg-slate-100"
              title="Tripletex-konfigurasjon (roter token + full sync)"
              data-testid="projects-tripletex-config-link"
            >
              <Settings className="w-4 h-4" />
            </Link>
            <button
              onClick={async () => {
                try {
                  toast.info("Henter nye aktive prosjekter…");
                  const { data } = await axios.post(`${API}/tripletex/sync?active_only=true`, {}, authHeader());
                  if (data.created === 0) {
                    toast.success("Alt er oppdatert — ingen nye aktive prosjekter");
                  } else {
                    toast.success(`${data.created} ${data.created === 1 ? "nytt prosjekt" : "nye prosjekter"} lagt til`);
                  }
                  load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Sync feilet");
                }
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-emerald-600 text-emerald-700 hover:bg-emerald-50 text-sm font-semibold"
              data-testid="projects-sync-btn"
              title="Hent nye aktive prosjekter fra Tripletex (rask og støyfri)"
            >
              <RefreshCw className="w-4 h-4" /> Sync aktive
            </button>
            <button onClick={() => setShowTripletex(true)} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-sm font-semibold" style={{ color: "#fff" }} data-testid="projects-tripletex-btn">
              <Download className="w-4 h-4" /> Tripletex
            </button>
            <button onClick={() => setDialog({})} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-slate-900 text-sm font-semibold" style={{ color: "#fff" }} data-testid="projects-new-btn">
              <Plus className="w-4 h-4" /> Nytt prosjekt
            </button>
          </>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : projects.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center">
          <FolderKanban className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm text-slate-500 mb-4">Ingen prosjekter funnet.</div>
          {user.is_admin && (
            <button onClick={() => setDialog({})} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-slate-900 text-sm font-semibold" style={{ color: "#fff" }} data-testid="projects-empty-add">
              <Plus className="w-4 h-4" /> Legg til første prosjekt
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid="projects-list">
          {projects.map((p) => {
            const st = STATUS_META[p.status] || STATUS_META.active;
            const secondary = p.customer_name || (p.name && p.name !== p.address ? p.name : "");
            return (
              <div key={p.id} className="flex items-center gap-3 px-3 py-2.5 border-b last:border-b-0 border-slate-100 hover:bg-slate-50 transition group" data-testid={`project-card-${p.id}`}>
                <Link to={`/doserne/prosjekter/${p.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${st.dot}`} title={st.label} />
                  {p.number && (
                    <span className="text-[10px] font-mono font-bold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 flex-shrink-0">{p.number}</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-black text-slate-900 truncate" data-testid={`project-title-${p.id}`}>
                      {p.address || p.name || "(uten adresse)"}
                    </div>
                    {(secondary || p.legacy) && (
                      <div className="text-[11px] text-slate-500 flex items-center gap-1 truncate">
                        {secondary && <><UserIcon className="w-2.5 h-2.5 flex-shrink-0" /><span className="truncate">{secondary}</span></>}
                        {p.legacy && <span className="text-[9px] uppercase tracking-widest text-slate-400 font-black ml-1">· Legacy</span>}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-700 flex-shrink-0" />
                </Link>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.dispatchEvent(new CustomEvent("doserne:open-project-chat", { detail: { project_id: p.id } }));
                  }}
                  className="w-8 h-8 rounded-lg bg-[#0084ff] hover:bg-[#0074e1] text-white flex items-center justify-center flex-shrink-0"
                  title="Prosjektchat"
                  data-testid={`project-chat-${p.id}`}
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                </button>
                {user.is_admin && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!p.legacy && (
                      <button onClick={() => setDialog(p)} className="w-7 h-7 rounded-md text-slate-400 hover:bg-slate-200 hover:text-slate-900 flex items-center justify-center" title="Rediger" data-testid={`project-edit-${p.id}`}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button onClick={() => del(p)} className="w-7 h-7 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center" title="Slett" data-testid={`project-delete-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {dialog !== null && <ProjectDialog initial={dialog.id ? dialog : null} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} authHeader={authHeader} />}
      {showTripletex && (
        <TripletexImportModal
          authHeader={authHeader}
          onClose={() => setShowTripletex(false)}
          onImported={load}
        />
      )}
    </DoserneShell>
  );
}
